import { ChangeDetectorRef, Component, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { HttpClient, HttpEventType, HttpRequest } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import { Editor, Toolbar } from 'ngx-editor';

import { Checkbox } from "../../shared/components/checkmarks/checkmarks.component";
import { ConfirmComponent } from "../../shared/components/confirm/confirm.component";
import { DialogService } from "../../shared/components/dialog/dialog.service";
import { AbstractInFunnelComponent } from "../../shared/components/funnel/abstract.in-funnel.component";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { UploadFileService } from "../../shared/components/upload-file/upload-file.service";
import { UploadProgress, UploadLargeFileComponent } from "../../shared/components/upload-large-file/upload-large-file.component";
import { FunnelService } from "../../shared/services/funnel.service";
import { capitalize } from "../../shared/tool-functions/capitalize";
import { decimalMinutesToHHMMSS, HHMMSSToDecimalMinutes } from "../../shared/tool-functions/date-tools";
import { ThematicRepository } from "../../thematics/repositories/thematic.repository";
import { contentFormats, pillars } from "../models/contents.entity";
import { ContentsRepository } from "../repositories/contents.repository";
import { getFormatNameBySlug } from "../utils/utils";

@Component({
    selector: "app-admin-create-content",
    templateUrl: "./admin-create-content.component.html",
    styleUrls: ["./admin-create-content.component.scss"],
})

export class AdminCreateContentComponent extends AbstractInFunnelComponent {
    // WYSIWYG Editor for description
    editor!: Editor;
    toolbar: Toolbar = [
      ["bold", "italic"],
      ["underline", "strike"],
      ["ordered_list", "bullet_list"],
      ["link"],
      ["align_left", "align_center", "align_right", "align_justify"],
    ];

    // Note: objective field removed per REMOVE-OBJECTIF.txt requirements
    // Note: pillar changed from multi-select to single-select per REMOVE-OBJECTIF.txt
    contentForm!: FormGroup<{
        title: FormControl<string>;
        description: FormControl<string>;
        format: FormControl<string>;
        readingDuration: FormControl<string>;
        resource: FormControl<string>;
        pillar: FormControl<string>;
    }>;

    content?: any;

    isEdition: boolean = false;

    formats = contentFormats;

    formatsNames = contentFormats.map(format => capitalize(format.label));

    // Pillar names for radio button selection (single pillar only)
    pillarNames = pillars.map(pillar => pillar.label);

    thematicsAssociatedCheckboxes: Checkbox[] = [];

    thematicsAssociated: string[] = [];

    // Note: categoriesAssociatedCheckboxes removed per REMOVE-OBJECTIF.txt requirements

    modification$ = new Subject<void>();

    // Note: pillarsAssociatedCheckboxes removed - now using single-select radio button

    file?: File;

    coverImage?: File;

    // Track if files have been modified (new file selected)
    fileModified: boolean = false;

    coverImageModified: boolean = false;

    errorMessages: string[] = [];

    format: string = '';

    loading = true;

    coverImageUrl = '';

    showCoverImageUrl = false;

    isValidForm = true;

    uploadProgress: UploadProgress | null = null;

    coverImageUploadProgress: UploadProgress | null = null;

    acceptingMimeTypes$: BehaviorSubject<string> = new BehaviorSubject<string>('.mp4,.avi,.mov');

    coverImageMimeTypes$: BehaviorSubject<string> = new BehaviorSubject<string>('.png,.jpg,.jpeg');

    @ViewChild(UploadLargeFileComponent) uploadLargeFileComponent!: UploadLargeFileComponent;

    private pendingUploads = 0;

    private uploadFailed = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly thematicRepository: ThematicRepository,
        protected override readonly route: ActivatedRoute,
        protected override readonly funnelService: FunnelService,
        private readonly uploadService: UploadFileService,
        private readonly snackBarService: SnackbarService,
        private readonly contentsRepository: ContentsRepository,
        private readonly dialog: DialogService,
        private cdr: ChangeDetectorRef,
        private readonly router: Router,
        private readonly http: HttpClient
    ) {
        super(funnelService, route);
    }

    init(){
        this.editor = new Editor();
        this.loading = true;

        this.coverImageUrl = this.content?.image ?? '';

        this.showCoverImageUrl = !!this.content?.image.trim().length;

        // Get the first pillar name for existing content (single pillar selection)
        const existingPillarName = this.content?.pillars?.[0]?.name
            ? pillars.find(p => p.key === this.content.pillars[0].name)?.label ?? ''
            : '';

        // Note: objective field removed per REMOVE-OBJECTIF.txt requirements
        this.contentForm = this.formBuilder.group({
            title: new FormControl<string>(this.content?.title ?? '', { validators: Validators.required, nonNullable: true }),
            description: new FormControl<string>(this.content?.description ?? '', { validators: Validators.required, nonNullable: true }),
            format: new FormControl<string>(getFormatNameBySlug(this.content?.format) ?? '', { validators: Validators.required, nonNullable: true }),
            readingDuration: new FormControl<string>(decimalMinutesToHHMMSS(this.content?.readingDuration) ?? 0, { validators: [Validators.required], nonNullable: true, }),
            resource: new FormControl<string>(this.content?.resource ?? '', { validators: Validators.required, nonNullable: true }),
            pillar: new FormControl<string>(existingPillarName, { validators: Validators.required, nonNullable: true }),
          });

        this.loading = false;

        this.funnelService.validate$.pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.save();
            this.funnelService.canValidate$.next(false);
          });

        this.modification$.next();
    }

    override ngOnDestroy(): void {
        this.editor?.destroy();
        super.ngOnDestroy();
    }

    override ngAfterViewInit() {
        super.ngAfterViewInit();
    
        setTimeout(() => {
          this.modification$
            .pipe(
              takeUntil(this.destroy$))
            .subscribe(() => {
              if (this.isValid()) {
                this.funnelService.canValidate$.next(true);
                this.funnelService.validateMessage$.next( "Enregistrer");
              } else {
                this.funnelService.canValidate$.next(false);
              }
            });
    
          this.modification$.next();
        }, 1000);
    
      }

    override ngOnInit(): void {
        this.route.data.pipe(
            takeUntil(this.destroy$)
        )
            .subscribe((data) => {

                    this.content = data.content;
                    this.isEdition = data.isEdition;

                    this.init();

                    this.previousMessage = 'Retour à la liste des ressources';

                    super.ngOnInit();

                    // Note: Categories removed per REMOVE-OBJECTIF.txt requirements
                    // Only fetch thematics now
                    this.acceptingMimeTypes$.next(this.getMimeTypeByFormatSlug(this.content?.format));

                    this.thematicRepository.getAll().subscribe(thematics => {
                        // Note: pillarsAssociatedCheckboxes removed - now using single-select radio button
                        this.thematicsAssociatedCheckboxes = thematics.map(thematic => ({
                            id: thematic.id,
                            key: thematic.name,
                            selected: this.content ? this.content.thematics.map((t: any) => t.id).includes(thematic.id) : false,
                        }));

                    });

                    this.contentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
                        this.modification$.next();
                      });

                      this.contentForm.get('format')?.valueChanges.subscribe(value => {
                        this.acceptingMimeTypes$.next(this.getMimeTypeByFormat(value));
                        this.format = value ?? '';
                        this.modification$.next();
                        this.cdr.detectChanges();
                      });

                    this.loading = false;
            });
    }

    isValid(): boolean {
        this.errorMessages = [];

        if (!this.contentForm.controls.title.valid) {
          this.errorMessages.push('Titre manquant');
        }
        if (!this.contentForm.controls.description.valid) {
          this.errorMessages.push('Description manquante');
        }
        if (!this.contentForm.controls.format.valid) {
          this.errorMessages.push('Format manquant');
        }
        if (!this.contentForm.controls.readingDuration.valid) {
          this.errorMessages.push('Temps de lecture manquant');
        }
        // Note: Category validation removed per REMOVE-OBJECTIF.txt requirements
        // Note: Pillar is now a single-select required field
        if (!this.contentForm.controls.pillar.valid || !this.contentForm.get('pillar')?.value?.length) {
          this.errorMessages.push('Pilier manquant');
        }
        if (!this.thematicsAssociated.length) {
          this.errorMessages.push('Thématique manquante');
        }
        if (!this.file && !this.contentForm.get('resource')?.value?.length && !this.content?.resource) {
          this.errorMessages.push('Fichier manquant');
        }
        if (!this.coverImage && !this.content?.image) {
          this.errorMessages.push('Image de couverture manquante');
        }
        return !this.errorMessages.length;
    }

    // Note: updateCategoriesAssociated removed per REMOVE-OBJECTIF.txt requirements
    // Note: updatePillarsAssociated removed - pillar is now single-select via form control

    updateThematicsAssociated(checkboxes: Checkbox[]){
        this.thematicsAssociated = checkboxes.filter(checkbox => checkbox.selected).map(checkbox => checkbox.id);
        this.modification$.next();
    }

    /**
     * Helper method to convert pillar label to key
     */
    private getPillarKeyByLabel(label: string): string {
        const pillar = pillars.find(p => p.label === label);
        return pillar?.key ?? '';
    }

    updateFile(file: File): void {
        this.file = file;
        this.fileModified = true; // Mark as modified when new file is selected
        this.modification$.next();
    }

    updateCoverImage(file: File): void {
        this.coverImage = file;
        this.coverImageModified = true; // Mark as modified when new cover image is selected
        this.showCoverImageUrl = false;
        this.coverImageUrl = '';
        this.modification$.next();
    }

    save(): void {
        if (!this.isValid()) {
          this.isValidForm = false;
          this.cdr.detectChanges();
          return;
        }

        this.funnelService.componentLoading$.next(true);
        this.isValidForm = true;

        // Get the pillar key from the selected label (single pillar selection)
        const selectedPillarLabel = this.contentForm.get('pillar')?.value ?? '';
        const pillarKey = this.getPillarKeyByLabel(selectedPillarLabel);

        // Note: objective and categories removed per REMOVE-OBJECTIF.txt requirements
        // Note: pillars is now a single value array (only one pillar can be selected)
        const result = {
          title: this.contentForm.get('title')?.value,
          description: this.contentForm.get('description')?.value,
          format: this.contentForm.get('format')?.value,
          readingDuration: HHMMSSToDecimalMinutes(this.contentForm.get('readingDuration')?.value ?? '00:00:00'),
          resource: this.contentForm.get('resource')?.value,
          pillars: pillarKey ? [pillarKey] : [], // Single pillar as array
          thematics: this.thematicsAssociated
        } as any;

        this.loading = true;

        if (this.content) {
          // Editing existing content
          this.contentsRepository.patch(this.content.id, result)
            .subscribe(content => {
              this.content = content;
              this.handleFileUploadsAfterContentSave(content);
            });
        } else {
          // Creating new content
          this.contentsRepository.create(result)
            .subscribe(content => {
              this.content = content;
              this.handleFileUploadsAfterContentSave(content);
            });
        }
    }

    private handleFileUploadsAfterContentSave(content: any): void {
        this.pendingUploads = 0;
        this.uploadFailed = false;

        // For updates: only upload files that have been modified
        // For creation: always upload files since they're new
        const shouldUploadMainFile = this.file && (!this.isEdition || this.fileModified);
        const shouldUploadCoverImage = this.coverImage && (!this.isEdition || this.coverImageModified);

        // Check if we need to upload main file via large file component
        if (shouldUploadMainFile && this.uploadLargeFileComponent && this.uploadLargeFileComponent.fileReady) {
          this.pendingUploads++;
          this.uploadLargeFileComponent.uploadFileToEntity(content.id);
        }
        // Fallback: upload via HttpRequest with progress tracking
        else if (shouldUploadMainFile && (!this.uploadLargeFileComponent || !this.uploadLargeFileComponent.fileReady)) {
          this.pendingUploads++;
          console.log('📤 Uploading main file via fallback:', this.file?.name);

          // Initialize upload progress
          this.uploadProgress = {
            progress: 0,
            status: 'uploading',
            fileName: this.file!.name,
            loaded: 0,
            total: this.file!.size,
            stage: 'uploading',
            statusMessage: 'Envoi du fichier...'
          };

          // Build FormData
          const formData = new FormData();
          formData.append('file', this.file!);

          // Get API URL from repository
          const apiUrl = (this.contentsRepository as any).apiUrl;
          const uploadUrl = `${apiUrl}/contents/${content.id}/image`;

          // Create HttpRequest with progress tracking
          const req = new HttpRequest('POST', uploadUrl, formData, {
            reportProgress: true
          });

          this.http.request(req)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (event: any) => {
                if (event.type === HttpEventType.UploadProgress && event.total) {
                  // Scale to 85% to leave room for server processing
                  this.uploadProgress!.progress = Math.round(85 * event.loaded / event.total);
                  this.uploadProgress!.loaded = event.loaded;
                  this.uploadProgress!.total = event.total;
                  this.uploadProgress!.stage = 'uploading';
                  this.uploadProgress!.statusMessage = `Envoi du fichier... ${this.uploadProgress!.progress}%`;
                  this.cdr.detectChanges();
                } else if (event.type === HttpEventType.Response) {
                  // Show processing stage
                  this.uploadProgress!.progress = 95;
                  this.uploadProgress!.status = 'processing';
                  this.uploadProgress!.stage = 'processing';
                  this.uploadProgress!.statusMessage = 'Finalisation...';
                  this.cdr.detectChanges();

                  // Complete after brief delay
                  setTimeout(() => {
                    console.log('✅ Main file uploaded successfully:', event.body);
                    this.uploadProgress!.progress = 100;
                    this.uploadProgress!.status = 'completed';
                    this.uploadProgress!.stage = 'finalizing';
                    this.uploadProgress!.statusMessage = 'Terminé!';
                    this.pendingUploads--;
                    this.checkUploadsComplete();
                    this.cdr.detectChanges();
                  }, 500);
                }
              },
              error: (error) => {
                console.error('❌ Main file upload failed:', error);
                this.uploadProgress!.status = 'error';
                this.uploadProgress!.error = 'Erreur lors de l\'upload';
                this.uploadFailed = true;
                this.pendingUploads--;
                this.checkUploadsComplete();
                this.cdr.detectChanges();
              }
            });
        }

        // Upload cover image with progress tracking
        if (shouldUploadCoverImage) {
          this.pendingUploads++;
          console.log('📤 Uploading cover image:', this.coverImage?.name);

          // Initialize cover image upload progress
          this.coverImageUploadProgress = {
            progress: 0,
            status: 'uploading',
            fileName: this.coverImage!.name,
            loaded: 0,
            total: this.coverImage!.size,
            stage: 'uploading',
            statusMessage: 'Envoi de l\'image de couverture...'
          };

          // Build FormData
          const formData = new FormData();
          formData.append('file', this.coverImage!);
          formData.append('contentFileType', 'cover');

          // Get API URL from repository
          const apiUrl = (this.contentsRepository as any).apiUrl;
          const uploadUrl = `${apiUrl}/contents/${content.id}/image`;

          // Create HttpRequest with progress tracking
          const req = new HttpRequest('POST', uploadUrl, formData, {
            reportProgress: true
          });

          this.http.request(req)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (event: any) => {
                if (event.type === HttpEventType.UploadProgress && event.total) {
                  // Scale to 85% to leave room for server processing
                  this.coverImageUploadProgress!.progress = Math.round(85 * event.loaded / event.total);
                  this.coverImageUploadProgress!.loaded = event.loaded;
                  this.coverImageUploadProgress!.total = event.total;
                  this.coverImageUploadProgress!.stage = 'uploading';
                  this.coverImageUploadProgress!.statusMessage = `Envoi de l'image... ${this.coverImageUploadProgress!.progress}%`;
                  this.cdr.detectChanges();
                } else if (event.type === HttpEventType.Response) {
                  // Show processing stage
                  this.coverImageUploadProgress!.progress = 95;
                  this.coverImageUploadProgress!.status = 'processing';
                  this.coverImageUploadProgress!.stage = 'processing';
                  this.coverImageUploadProgress!.statusMessage = 'Finalisation...';
                  this.cdr.detectChanges();

                  // Complete after brief delay
                  setTimeout(() => {
                    console.log('✅ Cover image uploaded successfully:', event.body);
                    this.coverImageUploadProgress!.progress = 100;
                    this.coverImageUploadProgress!.status = 'completed';
                    this.coverImageUploadProgress!.stage = 'finalizing';
                    this.coverImageUploadProgress!.statusMessage = 'Terminé!';
                    this.pendingUploads--;
                    this.checkUploadsComplete();
                    this.cdr.detectChanges();
                  }, 500);
                }
              },
              error: (error) => {
                console.error('❌ Cover image upload failed:', error);
                this.coverImageUploadProgress!.status = 'error';
                this.coverImageUploadProgress!.error = 'Erreur lors de l\'upload';
                this.uploadFailed = true;
                this.pendingUploads--;
                this.checkUploadsComplete();
                this.cdr.detectChanges();
              }
            });
        }

        // If no files to upload, complete immediately
        if (this.pendingUploads === 0) {
          this.checkUploadsComplete();
        }
    }

    private checkUploadsComplete(): void {
        if (this.pendingUploads === 0) {
          if (this.uploadFailed) {
            this.snackBarService.pushMessage('Ressource enregistrée mais échec d\'upload de certains fichiers', 'error');
            console.error('❌ Some uploads failed - content saved but files not attached');
          } else {
            this.snackBarService.pushMessage('Ressource enregistrée avec succès', 'success');
          }
          this.loading = false;
          this.funnelService.componentLoading$.next(false);
          this.router.navigate(['resources', 'admin-resources']);
        }
      }
    
      deleteContent(){
        this.dialog
        .open(ConfirmComponent, {
            title: "Êtes-vous sûr de vouloir supprimer cette ressource ?",
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((isValidated) => {
            if(isValidated){
                this.contentsRepository.deleteById(this.content.id).subscribe(() => {
                    this.snackBarService.pushMessage('Ressource supprimée', 'success');
                    this.router.navigate([ 'resources', 'admin-resources' ]);
                    this.funnelService.componentLoading$.next(false);
                  });
            }
        });
      }

      isVideoFormat(): boolean {
        return this.format === 'Vidéo';
      }
    
      isAudioFormat(): boolean {
        return this.format === 'Podcast';
      }
    
      isPdfFormat(): boolean {
        return this.format === 'Fiche pratique';
      }
    
      isImageFormat(): boolean {
        return this.format === 'Infographie';
      }

      getMimeTypeByFormatSlug(format: string): string {
        switch (format) {
          case 'video':
            return '.mp4,.avi,.mov';
          case 'podcast':
            return '.mp3,.wav,.ogg';
          case 'pratical_sheet':
            return '.pdf';
          case 'infography':
            return '.png,.jpg,.jpeg';
          default:
            return '.mp4,.avi,.mov';
        }
      }

      getMimeTypeByFormat(format: string): string {
        switch (format) {
          case 'Vidéo':
            return '.mp4,.avi,.mov';
          case 'Podcast':
            return '.mp3,.wav,.ogg';
          case 'Fiche pratique':
            return '.pdf';
          case 'Infographie':
            return '.png,.jpg,.jpeg';
          default:
            return '.mp4,.avi,.mov';
        }
      }

      // Large file upload event handlers

      onUploadComplete(event: { fileName: string; uploadId: string }): void {
        this.uploadProgress = null;
        this.pendingUploads--;
        this.checkUploadsComplete();
        this.modification$.next();
      }

      onUploadError(error: string): void {
        this.uploadProgress = null;
        this.snackBarService.pushMessage(`Erreur d'upload: ${error}`, 'error');
        this.pendingUploads--;
        this.checkUploadsComplete();
      }

      onUploadProgress(progress: UploadProgress): void {
        this.uploadProgress = progress;
        this.cdr.detectChanges();
      }
}

